import { MultiVoucherUsageConfirmation } from 'app/src/features/Shopping/Direct/MultiVoucherUsage/components/MultiVoucherUsageConfirmation'
import { MultiVoucherUsageInvoiceForm } from 'app/src/features/Shopping/Direct/MultiVoucherUsage/components/MultiVoucherUsageInvoiceForm'
import { MultiVoucherUsageSelection } from 'app/src/features/Shopping/Direct/MultiVoucherUsage/components/MultiVoucherUsageSelection'
import { MultiVoucherUsageSuccess } from 'app/src/features/Shopping/Direct/MultiVoucherUsage/components/MultiVoucherUsageSuccess'
import { RefundExternalKeysFilter } from 'app/src/features/Shopping/Refund/components/functions'
import { RefundFileUploader } from 'app/src/features/Shopping/Refund/components/RefundFileUploader'
import {
  AdditionalDocumentUploadStepName,
  ConfirmationStepName,
  DocumentScope,
  InvoiceUpdateStepName,
  InvoiceUploadStepName,
  KindPosteEnergia,
  KindPosteFibra,
  onFileLoaded,
  refundForwardAction,
  setRefundBulkFormData,
  setRefundFormData,
  SuccessFinalStepName,
  updateFiles,
  VoucherSelectionStepName,
} from 'app/src/features/Shopping/Refund/components/RefundFunctions'
import {
  checkVoucherEnough,
  fileStatusChecker,
  REGEX_Importo,
  type FileStepType,
  type FormComponentInfoType,
  type FormItemEnum,
  type SelectedVouchersType,
  type StatusCheckerInfoBundleType,
  type WizardStepConfigType,
} from 'app/src/features/Shopping/shared'
import type { DocumentFileType, MultiVoucherGroupedType, MultiVoucherSelectedType } from 'app/src/model'
import { refundApi, shoppingApi } from 'app/src/services/welfareportal'
import { BackToCatalogButton, ErrorPage, PageWidget, PortalConfirmDialog, PortalDialog } from 'app/src/shared/components'
import { decimalFormatter } from 'app/src/shared/helpers'
import { useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import { cloneDeep } from 'lodash'
import { DateTime } from 'luxon'
import { Button } from 'primereact/button'
import { ProgressBar } from 'primereact/progressbar'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { date, number, object, ref, string, type ObjectShape } from 'yup'

export function MultiVoucherUsageFunnel(props: { refund: refundApi.Refund }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher.Usage' })
  const navigate = useWelfarePortalNavigate()
  const context = useWebAppContext()

  const confirmRefundMutation = refundApi.useRefundRequestConfirm()
  const cancelRefundMutation = refundApi.useRefundRequestDelete()
  const creditApiResponse = shoppingApi.useCreditGet(
    {},
    {
      meta: {
        skip404Errors: true,
      },
    },
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formData, setFormData] = useState<any[]>()

  const [showAbortRefundModal, setShowAbortRefundModal] = useState<boolean>(false)
  const [showConfirmRefundModal, setShowConfirmRefundModal] = useState<boolean>(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formErrors, setFormErrors] = useState<any>()
  const [forwardButtonDisabled, setForwardButtonDisabled] = useState<boolean>(false)
  const [forwardActionRequested, setForwardActionRequested] = useState<boolean>(false)
  const [additionalRadioKeys, setAdditionalRadioKeys] = useState<string[]>()
  const [privacyStatementAccept, setPrivacyStatementAccept] = useState<boolean>(false)
  const [showAdditionalDocumentsModal, setShowAdditionalDocumentsModal] = useState<boolean>(false)

  const [genericError, setGenericError] = useState<JSX.Element | string>()

  const [files, setFiles] = useState<FileStepType[]>()
  const wizardSteps = useMemo(() => {
    const steps: WizardStepConfigType[] = []
    const invoiceDocument = props.refund.rule?.documents.find((x) => x.types.some((y) => y === 'invoice'))
    const additionalDocuments = props.refund.rule?.documents?.find((x) => x.types.some((y) => y === 'none'))
    let yupSchema

    let invoiceYupShape: Record<string, unknown>

    switch (props.refund.kind) {
      case KindPosteFibra:
        invoiceYupShape = {
          invoiceId: string().required(t('Kinds')[props.refund.kind].Errors.Required.invoiceId),
          providerId: string().required(t('Kinds')[props.refund.kind].Errors.Required.providerId),
          domiciliation: string()
            .required(t('Kinds')[props.refund.kind].Errors.Required.domiciliation)
            .test({
              exclusive: false,
              test(value) {
                return value === 'true'
              },
              message: { isBlocking: true, body: t('Kinds')[props.refund.kind].Errors.Custom.domiciliationBlocking },
            }),
          dueDate: date()
            .required(t('Kinds')[props.refund.kind].Errors.Required.dueDate)
            .test({
              exclusive: false,
              test(date) {
                const dt = Math.ceil(
                  DateTime.fromJSDate(date).diff(DateTime.fromJSDate(new Date()), ['days']).toObject().days ?? 0,
                )
                return dt >= 0
              },
              message: { isBlocking: true, body: t('Kinds')[props.refund.kind].Errors.Custom.expired },
            })
            .test({
              exclusive: false,
              test(value) {
                const invoiceConstraintDueDate = props.refund.rule?.invoiceConstraints?.find(
                  (x) => x.key === 'dueDate' && x.operator === 'maxDays',
                )
                const days = invoiceConstraintDueDate ? parseInt(invoiceConstraintDueDate.value) : 10

                return (
                  Math.ceil(DateTime.fromJSDate(value).diff(DateTime.fromJSDate(new Date()), ['days']).toObject().days ?? 0) >=
                  days
                )
              },
              message: { isBlocking: true, body: t('Kinds')[props.refund.kind].Errors.Custom.expiring },
            }),
          invoiceTotal: string()
            .required(t('Kinds')[props.refund.kind].Errors.Required.invoiceTotal)
            .matches(REGEX_Importo, t('Kinds')[props.refund.kind].Errors.Matches.invoiceTotal),
          requestedRefund: string()
            .required(t('Kinds')[props.refund.kind].Errors.Required.requestedRefund)
            .matches(REGEX_Importo, t('Kinds')[props.refund.kind].Errors.Matches.requestedRefund),
          invoiceTotalNumber: number()
            .required(t('Kinds')[props.refund.kind].Errors.Required.invoiceTotal)
            .min(0.01, t('Kinds')[props.refund.kind].Errors.Min.invoiceTotalNumber),
          requestedRefundNumber: number()
            .required(t('Kinds')[props.refund.kind].Errors.Required.requestedRefund)
            .min(0.01, t('Kinds')[props.refund.kind].Errors.Min.requestedRefundNumber)
            .max(ref('invoiceTotalNumber'), t('Kinds')[props.refund.kind].Errors.Max.requestedRefundNumber),
        }
        break
      case KindPosteEnergia:
        invoiceYupShape = {
          invoiceId: string().required(t('Kinds')[props.refund.kind].Errors.Required.invoiceId),
          customerId: string().required(t('Kinds')[props.refund.kind].Errors.Required.customerId),
          invoiceDate: date()
            .required(t('Kinds')[props.refund.kind].Errors.Required.invoiceDate)
            .max(new Date(), t('Kinds')[props.refund.kind].Errors.Max.invoiceDate),
          domiciliation: string()
            .required(t('Kinds')[props.refund.kind].Errors.Required.domiciliation)
            .test({
              exclusive: false,
              test(value) {
                return value === 'true'
              },
              message: { isBlocking: true, body: t('Kinds')[props.refund.kind].Errors.Custom.domiciliationBlocking },
            }),
          dueDate: date()
            .required(t('Kinds')[props.refund.kind].Errors.Required.dueDate)
            .test({
              exclusive: false,
              test(date) {
                const dt = Math.ceil(
                  DateTime.fromJSDate(date).diff(DateTime.fromJSDate(new Date()), ['days']).toObject().days ?? 0,
                )
                return dt >= 0
              },
              message: { isBlocking: true, body: t('Kinds')[props.refund.kind].Errors.Custom.expired },
            })
            .test({
              exclusive: false,
              test(value) {
                const invoiceConstraintDueDate = props.refund.rule?.invoiceConstraints?.find(
                  (x) => x.key === 'dueDate' && x.operator === 'maxDays',
                )
                const days = invoiceConstraintDueDate ? parseInt(invoiceConstraintDueDate.value) : 10

                return (
                  Math.ceil(DateTime.fromJSDate(value).diff(DateTime.fromJSDate(new Date()), ['days']).toObject().days ?? 0) >=
                  days
                )
              },
              message: { isBlocking: true, body: t('Kinds')[props.refund.kind].Errors.Custom.expiring },
            }),
          invoiceTotal: string()
            .required(t('Kinds')[props.refund.kind].Errors.Required.invoiceTotal)
            .matches(REGEX_Importo, t('Kinds')[props.refund.kind].Errors.Matches.invoiceTotal),
          requestedRefund: string()
            .required(t('Kinds')[props.refund.kind].Errors.Required.requestedRefund)
            .matches(REGEX_Importo, t('Kinds')[props.refund.kind].Errors.Matches.requestedRefund),
          invoiceTotalNumber: number()
            .required(t('Kinds')[props.refund.kind].Errors.Required.invoiceTotal)
            .min(0.01, t('Kinds')[props.refund.kind].Errors.Min.invoiceTotalNumber),
          requestedRefundNumber: number()
            .required(t('Kinds')[props.refund.kind].Errors.Required.requestedRefund)
            .min(0.01, t('Kinds')[props.refund.kind].Errors.Min.requestedRefundNumber)
            .max(ref('invoiceTotalNumber'), t('Kinds')[props.refund.kind].Errors.Max.requestedRefundNumber),
        }
        invoiceYupShape[
          t('Kinds')[props.refund.kind].SubCategories![props.refund.attributes?.subCategoryId ?? ''].contractIdFieldName
        ] = string().required(
          t('Kinds')[props.refund.kind].SubCategories![props.refund.attributes?.subCategoryId].Errors.Required.contractId,
        )
        break
      default:
        context.logger?.error('Unrecognized Kind', { KIND: props.refund.kind })
        context.showToastMessage({ severity: 'error', content: t('Errors.UnrecognizedKind') })
        throw new Error()
    }

    if (
      props.refund.rule?.externalKeys &&
      props.refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, props.refund))
    ) {
      const externalKeys: string[] = []
      let externalKeysLabel = ''
      props.refund.rule.externalKeys
        .filter((key) => RefundExternalKeysFilter(key, props.refund))
        .forEach((externalKey, index) => {
          externalKeys.push(externalKey.key + 'Number')

          if (index > 0) externalKeysLabel += ','

          externalKeysLabel += externalKey.label

          invoiceYupShape[externalKey.key + 'Number'] = number().required(
            t('Errors.Required.customExternalKey', { KEY: externalKey.key }),
          )
          invoiceYupShape[externalKey?.key ?? ''] = string()
            .required(t('Errors.Required.customExternalKey', { KEY: externalKey.key }))
            .matches(REGEX_Importo, t('Errors.Matches.customExternalKey'))
        })

      yupSchema = object()
        .shape(invoiceYupShape as ObjectShape)
        .test({
          message: t('Errors.Custom.customExternalKey', { LABEL: externalKeysLabel }),
          name: 'externalKeysSum',
          test: ({ ...data }) => {
            let sum: number = data.requestedRefundNumber as number
            if (externalKeys.some((x) => !data[x])) return true

            externalKeys.forEach((externalKey) => (sum += data[externalKey] as number))
            return sum <= (data.invoiceTotalNumber as number)
          },
        })
    } else {
      yupSchema = object().shape(invoiceYupShape as ObjectShape)
    }

    if (invoiceDocument) {
      const invoiceStep: WizardStepConfigType = {
        documentAttributes: {
          documentScope: DocumentScope,
          documentExtractionType: invoiceDocument.extractionType ?? invoiceDocument.types[0],
          max: 1,
          min: 1,
        },
        stepName: InvoiceUploadStepName,
        title: t('Funnel.Steps.InvoiceUpload.title', { REFUNDCATEGORY: props.refund.category }),
        descriptionPrefix: <div dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.InvoiceUpload.descriptionPrefix') }} />,
        description: invoiceDocument?.description,
        statusChecker: fileStatusChecker,
      }

      const invoiceUpdateStep: WizardStepConfigType = {
        title: t('Funnel.Steps.InvoiceUpdate.title', { REFUNDCATEGORY: props.refund.category }),
        descriptionPrefix: <div dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.InvoiceUpdate.descriptionPrefix') }} />,
        description: t('Funnel.Steps.InvoiceUpdate.description', { REFUNDCATEGORY: props.refund.category }),
        canAbortRequest: true,
        stepName: InvoiceUpdateStepName,
        formComponentInfo: {
          fileStepName: InvoiceUploadStepName,
          documentType: invoiceDocument.extractionType ?? '',
          yupSchema,
          initializeForm: (file: DocumentFileType, credit_: number) => {
            const invoiceTotal = file?.documentDetails?.data?.invoiceTotal?.value
              ? decimalFormatter.format(file.documentDetails.data.invoiceTotal.value)
              : ''
            let retObj: Record<string, unknown>

            switch (props.refund.kind) {
              case KindPosteFibra:
                retObj = {
                  invoiceId: file?.documentDetails?.data?.invoiceId?.value,
                  invoiceTotal,
                  invoiceTotalNumber: file?.documentDetails?.data?.invoiceTotal?.value ?? 0,
                  requestedRefundNumber: file?.documentDetails?.data?.invoiceTotal?.value ?? 0,
                  requestedRefund: invoiceTotal ?? '0',
                  dueDate: file?.documentDetails?.data?.dueDate?.value,
                  domiciliation: file?.documentDetails?.data?.domiciliation?.value,
                  providerid: file?.documentDetails?.data?.providerid?.value,
                  welfareCreditNumber: credit_,
                }
                break
              case KindPosteEnergia:
                retObj = {
                  invoiceId: file?.documentDetails?.data?.invoiceId?.value,
                  customerId: file?.documentDetails?.data?.customerId?.value,
                  invoiceDate: file?.documentDetails?.data?.invoiceDate?.value,
                  invoiceTotal,
                  domiciliation: file?.documentDetails?.data?.domiciliation?.value,
                  dueDate: file?.documentDetails?.data?.dueDate?.value,
                  invoiceTotalNumber: file?.documentDetails?.data?.invoiceTotal?.value ?? 0,
                  requestedRefundNumber: file?.documentDetails?.data?.invoiceTotal?.value ?? 0,
                  requestedRefund: invoiceTotal ?? '0',
                  welfareCreditNumber: credit_,
                }

                retObj[t('Kinds')[props.refund.kind].SubCategories![props.refund.attributes?.subCategoryId].contractIdFieldName] =
                  file?.documentDetails?.data?.[
                    t('Kinds')[props.refund.kind].SubCategories![props.refund.attributes?.subCategoryId].contractIdFieldName
                  ]?.value

                break
              default:
                context.logger?.error('Unrecognized Kind', { KIND: props.refund.kind })
                context.showToastMessage({ severity: 'error', content: t('Errors.UnrecognizedKind') })
                throw new Error()
            }

            if (
              props.refund.rule?.externalKeys?.some((key: refundApi.AdditionalKey) => RefundExternalKeysFilter(key, props.refund))
            ) {
              props.refund.rule.externalKeys
                .filter((key: refundApi.AdditionalKey) => RefundExternalKeysFilter(key, props.refund))
                .forEach((externalKey: refundApi.AdditionalKey) => {
                  const fieldValue = file?.documentDetails?.data?.[externalKey.key ?? '']?.value
                  if (fieldValue) {
                    retObj[externalKey.key + 'Number'] = fieldValue
                    retObj[externalKey.key ?? ''] = decimalFormatter.format(fieldValue)

                    if (invoiceTotal) {
                      ;(retObj.requestedRefundNumber as number) -= fieldValue
                    }
                  }
                })

              retObj.requestedRefund = retObj.requestedRefundNumber
                ? decimalFormatter.format(retObj.requestedRefundNumber as number)
                : '0'
            }

            return retObj
          },
        },
      }
      steps.push(...[invoiceStep, invoiceUpdateStep])

      if (additionalDocuments) {
        steps.push({
          documentAttributes: {
            documentScope: DocumentScope,
            documentExtractionType: additionalDocuments.extractionType ?? additionalDocuments.types[0],
            max: additionalDocuments.max,
            min: additionalDocuments.min,
          },
          stepName: AdditionalDocumentUploadStepName,
          canAbortRequest: true,
          title: t('Funnel.Steps.AdditionalDocumentsUpload.title', { REFUNDCATEGORY: props.refund.category }),
          description: additionalDocuments?.description,
          descriptionModal: additionalDocuments?.descriptionModal,
          statusChecker: fileStatusChecker,
        })
      }
      steps.push({
        stepName: VoucherSelectionStepName,
        canAbortRequest: true,
        title: t('Funnel.Steps.VoucherSelection.title', { REFUNDCATEGORY: props.refund.category }),
        description: t('Funnel.Steps.VoucherSelection.description', { REFUNDCATEGORY: props.refund.category }),
        statusChecker: checkVoucherEnough,
        multiVoucherStep: true,
        confirmationStep: false,
      })

      steps.push(
        {
          stepName: ConfirmationStepName,
          canAbortRequest: true,
          title: t('Funnel.Steps.ConfirmationStep.title', { REFUNDCATEGORY: props.refund.category }),
          statusChecker: (_step: WizardStepConfigType, checkerBundleInfo: StatusCheckerInfoBundleType) =>
            (checkerBundleInfo.privacyStatementAccept || typeof props.refund?.rule?.termsAndConditions === 'undefined') &&
            (!props?.refund?.rule?.additionalAttributes?.radio ||
              props?.refund?.rule?.additionalAttributes?.radio.length ===
                checkerBundleInfo?.additionalRadioKeys?.filter((x) => typeof x !== 'undefined').length),
          confirmationStep: true,
        },
        {
          stepName: SuccessFinalStepName,
        },
      )

      return steps
    }
    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refund])

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0)

  useEffect(() => {
    if (wizardSteps) {
      setFormData(wizardSteps.map(() => undefined))
      setFiles(wizardSteps.map((x) => ({ stepName: x.stepName, files: [] })))
    }
  }, [wizardSteps])

  const currentStep = useMemo(() => {
    return wizardSteps?.length > 0 && currentStepIndex >= 0 && wizardSteps?.[currentStepIndex]
      ? wizardSteps[currentStepIndex]
      : undefined
  }, [currentStepIndex, wizardSteps])

  //Voucher Hook
  const voucherOrderResponse = shoppingApi.useOrderList(
    { queryParams: { status: ['confirmed'], additionalInfoVirtualMoney: true } },
    { enabled: currentStep?.stepName === VoucherSelectionStepName },
  )
  const groupedAvailableVouchers: MultiVoucherGroupedType[] | undefined = useMemo(() => {
    if (!voucherOrderResponse?.isError && voucherOrderResponse.data) {
      if (voucherOrderResponse.data.items?.length === 0) return []
      else {
        const groupByResult = voucherOrderResponse.data.items.reduce<{
          [key: string]: { count: number; value: number }
        }>((acc, { priceDetails }) => {
          const priceDetailsDefined = priceDetails!
          acc[priceDetailsDefined.grossPrice] = acc[priceDetailsDefined.grossPrice] || {
            value: priceDetailsDefined.grossPrice,
            count: 0,
          }
          acc[priceDetailsDefined.grossPrice].count += 1

          return acc
        }, {})
        const vouchOptions: MultiVoucherGroupedType[] = Object.keys(groupByResult)
          .map((groupKey) => ({
            availableQuantity: groupByResult[groupKey].count,
            value: groupByResult[groupKey].value,
          }))
          .sort((a, b) => (a.value < b.value ? 1 : -1))
        return vouchOptions
      }
    } else return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherOrderResponse?.data])

  const [groupedSelectedVouchers, setGroupedSelectedVouchers] = useState<MultiVoucherSelectedType[]>()

  const selectedVoucherInfo: SelectedVouchersType = useMemo(() => {
    const selectedVoucherInfo_: SelectedVouchersType = {
      ids: [],
      total: 0,
    }

    if (
      groupedSelectedVouchers &&
      groupedSelectedVouchers.length > 0 &&
      voucherOrderResponse.data?.items &&
      voucherOrderResponse.data.items.length > 0
    ) {
      groupedSelectedVouchers
        .filter((x) => x.selectedQuantity > 0)
        .forEach((voucherListItem) => {
          const bucket = voucherOrderResponse.data.items
            ?.filter((x) => x.priceDetails?.grossPrice === voucherListItem.value)
            ?.map((x) => x.orderId)
            ?.slice(0, voucherListItem.selectedQuantity)

          selectedVoucherInfo_.ids.push(...bucket)
          selectedVoucherInfo_.total += voucherListItem.selectedQuantity * voucherListItem.value
        })
    }
    return selectedVoucherInfo_
  }, [groupedSelectedVouchers, voucherOrderResponse.data])

  useEffect(() => {
    setGroupedSelectedVouchers(groupedAvailableVouchers?.map((avVoucher) => ({ selectedQuantity: 0, value: avVoucher.value })))
  }, [groupedAvailableVouchers])

  const credit = useMemo(() => {
    const limitType = props.refund?.attributes?.['limitType'] ?? ''
    return !limitType ||
      creditApiResponse.isError ||
      !creditApiResponse?.data?.creditByServiceCategory ||
      !creditApiResponse?.data?.creditByServiceCategory[limitType]
      ? 0
      : (creditApiResponse?.data?.creditByServiceCategory[limitType].available ?? 0)
  }, [creditApiResponse, props.refund])

  const initializeForm = useCallback(
    (formComponentInfo: FormComponentInfoType) => {
      const fileEntry = files?.find((x) => x.stepName === formComponentInfo.fileStepName)
      if (!fileEntry || !fileEntry.files) return {}
      else {
        const docFile = fileEntry.files.find((x) => x.documentType === formComponentInfo.documentType)
        if (!docFile) return {}
        else return formComponentInfo.initializeForm(docFile, credit)
      }
    },
    [credit, files],
  )
  const getTargetComponentFormFile = useCallback(
    (_files: FileStepType[] | undefined, formComponent: FormComponentInfoType) =>
      _files
        ?.find((x) => formComponent.fileStepName === x.stepName)
        ?.files.find((x) => x.documentType === formComponent.documentType),
    [],
  )

  useEffect(() => {
    if (forwardActionRequested) {
      setForwardButtonDisabled(true)
      return
    }

    const currentStep_ =
      wizardSteps?.length > 0 && currentStepIndex >= 0 && wizardSteps?.[currentStepIndex]
        ? wizardSteps[currentStepIndex]
        : undefined

    if (currentStep_) {
      setForwardButtonDisabled(
        currentStep_.statusChecker
          ? !currentStep_.statusChecker(currentStep_, {
              wizardSteps,
              filesToCheck: files!,
              privacyStatementAccept:
                privacyStatementAccept ||
                typeof props.refund.rule?.termsAndConditions === 'undefined' ||
                !props.refund.rule?.termsAndConditions,
              additionalRadioKeys,
              selectedVoucherInfo,
              formData: formData!,
            })
          : false,
      )
    } else setForwardButtonDisabled(false)
  }, [
    additionalRadioKeys,
    currentStepIndex,
    files,
    props.refund.rule?.termsAndConditions,
    formData,
    forwardActionRequested,
    privacyStatementAccept,
    wizardSteps,
    selectedVoucherInfo,
  ])

  useEffect(() => {
    if (forwardActionRequested) {
      refundForwardAction(
        context,
        confirmRefundMutation,
        wizardSteps,
        currentStep!,
        currentStepIndex,
        setCurrentStepIndex,
        formData!,
        props.refund,
        initializeForm,
        setFormErrors,
        setForwardActionRequested,
        setFormData,
        files!,
        setFiles,
        getTargetComponentFormFile,
        additionalRadioKeys!,
        setGenericError,
        selectedVoucherInfo?.ids ?? [],
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forwardActionRequested])

  useEffect(() => {
    if (formErrors && formErrors._blockingMessages?.length > 0) {
      context.showToastMessage({ severity: 'error', content: formErrors._blockingMessages[0] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors])

  const showHeader = useMemo(() => {
    return (
      !genericError &&
      wizardSteps &&
      wizardSteps.length > 0 &&
      currentStep?.stepName !== wizardSteps[wizardSteps?.length - 1].stepName
    )
  }, [currentStep?.stepName, genericError, wizardSteps])

  const onGoBackButton = useCallback(() => {
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1)
    else setShowAbortRefundModal(true)
  }, [currentStepIndex])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStepIndex])

  useEffect(() => {
    if (confirmRefundMutation.isPending) window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [confirmRefundMutation.isPending])
  return (
    <PageWidget>
      {!confirmRefundMutation.isPending ? (
        <>
          {showAbortRefundModal && (
            <PortalConfirmDialog
              accept={async () => {
                try {
                  await cancelRefundMutation.mutateAsync({ pathParams: { refundId: props.refund.refundId } })
                } catch (error: unknown) {
                  context.logger?.error('Error Deleting Refund', { refundId: props.refund.refundId, error })
                } finally {
                  setShowAbortRefundModal(false)
                  navigate.goToCatalog()
                }
              }}
              reject={() => setShowAbortRefundModal(false)}
              body={<div dangerouslySetInnerHTML={{ __html: t('Funnel.Dialogs.Abort.body') }} />}
              title={t('Funnel.Dialogs.Abort.title')}
            />
          )}
          {showConfirmRefundModal && (
            <PortalConfirmDialog
              accept={() => {
                setForwardActionRequested(true)
              }}
              reject={() => setShowConfirmRefundModal(false)}
              yesLoading={confirmRefundMutation?.isPending}
              body={<div dangerouslySetInnerHTML={{ __html: t('Funnel.Dialogs.Confirm.body') }} />}
              title={t('Funnel.Dialogs.Confirm.title')}
            />
          )}

          {showAdditionalDocumentsModal && currentStep?.descriptionModal && (
            <PortalDialog
              onHide={() => setShowAdditionalDocumentsModal(false)}
              headerProps={{ title: t('Funnel.Dialogs.AdditionalDocuments.title') }}
              footerProps={{
                okLabel: t('Funnel.Dialogs.AdditionalDocuments.okButtonLabel'),
                okCallBack: () => setShowAdditionalDocumentsModal(false),
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: currentStep.descriptionModal }} />
            </PortalDialog>
          )}
          {showHeader && (
            <Button link onClick={onGoBackButton} icon='pi pi-arrow-left' pt={{ root: { className: 'mb-2' } }}>
              <span className='ml-2 font-bold'>
                {currentStepIndex === 0 ? t('Funnel.abortRequestBtnLabel') : t('Funnel.goBackBtnLabel')}
              </span>
            </Button>
          )}
          {!genericError && showHeader && (
            <div>
              <p className='mb-2 font-bold'>
                {t('Funnel.wizardTitle', { STEPNUMBER: currentStepIndex + 1, STEPCOUNT: wizardSteps?.length - 1 })}
              </p>

              <ProgressBar
                value={((currentStepIndex + 1) / (wizardSteps.length - 1)) * 100}
                showValue={false}
                className='h-[10px] rounded-none'
              />
            </div>
          )}
          {genericError && (
            <ErrorPage>
              <div className='mx-auto mt-3'>{genericError}</div>
              <div className='mx-auto flex justify-center'>
                <BackToCatalogButton />
              </div>
            </ErrorPage>
          )}

          {currentStep && !genericError && (
            <>
              {currentStep.title && (
                <div className='my-4 text-2xl font-bold' dangerouslySetInnerHTML={{ __html: currentStep.title }} />
              )}
              {currentStep.descriptionPrefix && <div className='mb-4 mt-4'>{currentStep.descriptionPrefix}</div>}
              {currentStep.description && (
                <div className='mb-4 mt-4' dangerouslySetInnerHTML={{ __html: `<div>${currentStep.description}</div>` }}></div>
              )}
              {currentStep.descriptionModal && (
                <div>
                  <span className='text-base'>
                    {t('Funnel.Dialogs.AdditionalDocuments.requestButtonLabelPrefix')}
                    <Button
                      role='link'
                      text
                      pt={{ root: { className: 'underline pb-0  pl-0 ml-2' } }}
                      link
                      onClick={() => setShowAdditionalDocumentsModal(true)}
                    >
                      {t('Funnel.Dialogs.AdditionalDocuments.requestButtonLabel')}
                    </Button>
                  </span>
                </div>
              )}
              {currentStep.documentAttributes && (
                <RefundFileUploader
                  refundId={props.refund.refundId}
                  analyzeBlocking
                  onDeletedFile={(documentId: string) =>
                    updateFiles(
                      files!,
                      setFiles,
                      currentStep.stepName,
                      files
                        ?.find((x) => x.stepName === currentStep?.stepName)
                        ?.files?.filter((x) => x.documentId !== documentId) ?? [],
                    )
                  }
                  onFileLoaded={(file: DocumentFileType) =>
                    onFileLoaded(wizardSteps, currentStep, files!, setFiles, file, formData!, setFormData)
                  }
                  files={files?.find((x) => x.stepName === currentStep.stepName)?.files ?? []}
                  documentAttributes={currentStep?.documentAttributes}
                />
              )}
              {currentStep.formComponentInfo && (
                <MultiVoucherUsageInvoiceForm
                  file={getTargetComponentFormFile(files, currentStep?.formComponentInfo)}
                  refund={props.refund}
                  formData={formData?.[currentStepIndex] ?? initializeForm(currentStep.formComponentInfo)}
                  formErrors={formErrors}
                  setFormData={(fieldId: string, fieldValue: unknown, type: FormItemEnum) =>
                    setRefundFormData(
                      currentStep,
                      currentStepIndex,
                      formData!,
                      initializeForm,
                      setFormData,
                      fieldId,
                      fieldValue,
                      type,
                    )
                  }
                  resetForm={() => {
                    const formData_ = cloneDeep(formData)
                    if (currentStep.formComponentInfo && formData_)
                      formData_[currentStepIndex] = initializeForm(currentStep.formComponentInfo)
                    setFormData(formData_)
                  }}
                  setBulkFormData={(fieldIds: string[], fieldValues: unknown[], types: FormItemEnum[]) =>
                    setRefundBulkFormData(
                      currentStep,
                      currentStepIndex,
                      formData!,
                      initializeForm,
                      setFormData,
                      fieldIds,
                      fieldValues,
                      types,
                    )
                  }
                />
              )}
              {typeof currentStep?.multiVoucherStep !== 'undefined' && currentStep?.multiVoucherStep && (
                <MultiVoucherUsageSelection
                  groupedSelectedVouchers={groupedSelectedVouchers}
                  availableVouchers={groupedAvailableVouchers}
                  setGroupedSelectedVouchers={setGroupedSelectedVouchers}
                  requestedRefund={
                    formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].requestedRefundNumber
                  }
                />
              )}
              {typeof currentStep?.confirmationStep !== 'undefined' && currentStep?.confirmationStep && (
                <MultiVoucherUsageConfirmation
                  refund={props.refund}
                  voucherAmount={selectedVoucherInfo?.total}
                  privacyStatementAccept={privacyStatementAccept}
                  setPrivacyStatementAccept={setPrivacyStatementAccept}
                  additionalRadioKeys={additionalRadioKeys}
                  setAdditionalRadioKeys={(radioGroupIndex: number, selectedKey: string) => {
                    const additionalRadioKeysCloned = cloneDeep(additionalRadioKeys) ?? []
                    additionalRadioKeysCloned[radioGroupIndex] = selectedKey
                    setAdditionalRadioKeys(additionalRadioKeysCloned)
                  }}
                  formData={{
                    requestedRefund:
                      formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].requestedRefundNumber,
                    documentDate: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].invoiceDate,
                    invoiceId: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].invoiceId,
                    vendorName: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].vendorName,
                    vendorTaxId: formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].vendorTaxId,
                    invoiceTotal:
                      formData?.[wizardSteps?.findIndex((x) => x.stepName === InvoiceUpdateStepName)].invoiceTotalNumber,
                  }}
                />
              )}
              {currentStep?.stepName === wizardSteps[wizardSteps?.length - 1].stepName && <MultiVoucherUsageSuccess />}
              {currentStepIndex < wizardSteps?.length - 1 && (
                <div className='mt-8 flex justify-center'>
                  <Button
                    disabled={forwardButtonDisabled}
                    onClick={() => {
                      if (typeof currentStep.confirmationStep !== 'undefined' && currentStep.confirmationStep)
                        setShowConfirmRefundModal(true)
                      else setForwardActionRequested(true)
                    }}
                  >
                    {currentStep.confirmationStep ? t('Funnel.confirmBtnLabel') : t('Funnel.continueBtnLabel')}
                  </Button>
                </div>
              )}
              {typeof currentStep?.canAbortRequest !== 'undefined' && currentStep?.canAbortRequest && (
                <div className='mt-8 flex justify-center'>
                  <Button onClick={() => setShowAbortRefundModal(true)} link>
                    <b>{t('Funnel.abortRequestBtnLabel')}</b>
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className='mt-2 flex'>
          <div className='mx-auto'>
            <ProgressSpinner data-testid='loading-element' className='flex' />
            <div className='mt-4' dangerouslySetInnerHTML={{ __html: t('Funnel.requestInProgressDescription') }}></div>
          </div>
        </div>
      )}
    </PageWidget>
  )
}
